import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth.store';

// ============================================
// Customer Hooks
// ============================================

/**
 * Fetch KYC status with SLA estimation
 */
export function useKycStatus(virtualAccountId?: string) {
  return useQuery({
    queryKey: ['kyc-status', virtualAccountId],
    queryFn: async () => {
      if (!virtualAccountId) return null;
      const response = await api.get(`/kyc/my-status/${virtualAccountId}`);
      return response.data;
    },
    enabled: !!virtualAccountId,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch required documents for KYC
 */
export function useRequiredDocuments() {
  return useQuery({
    queryKey: ['kyc-required-documents'],
    queryFn: async () => {
      const response = await api.get('/kyc/required-documents');
      return response.data;
    },
  });
}

/**
 * Upload a KYC document
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async ({
      file,
      kycType,
      documentTypeCode,
    }: {
      file: File;
      kycType: 'ORIGIN' | 'DESTINATION';
      documentTypeCode: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kycType', kycType);
      formData.append('documentTypeCode', documentTypeCode);
      formData.append('virtualAccountId', user?.virtualAccountId || '');

      const response = await api.post('/kyc/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
    },
  });
}

/**
 * Submit KYC for review
 */
export function useSubmitKyc() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (kycType: 'ORIGIN' | 'DESTINATION') => {
      const response = await api.post('/kyc/submit', {
        kycType,
        virtualAccountId: user?.virtualAccountId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
    },
  });
}

/**
 * Resubmit KYC after rejection
 */
export function useResubmitKyc() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (kycType: 'ORIGIN' | 'DESTINATION') => {
      const response = await api.post('/kyc/resubmit', {
        kycType,
        virtualAccountId: user?.virtualAccountId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
    },
  });
}

// ============================================
// Admin Hooks - Rejection Templates
// ============================================

/**
 * Fetch rejection templates for the tenant
 */
export function useRejectionTemplates() {
  return useQuery({
    queryKey: ['kyc-rejection-templates'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/settings/rejection-templates');
      return response.data;
    },
  });
}

/**
 * Create a rejection template
 */
export function useCreateRejectionTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: { title: string; reason: string; isActive?: boolean }) => {
      const response = await api.post('/admin/kyc/settings/rejection-templates', dto);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-rejection-templates'] });
    },
  });
}

/**
 * Update a rejection template
 */
export function useUpdateRejectionTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...dto
    }: {
      id: string;
      title?: string;
      reason?: string;
      isActive?: boolean;
    }) => {
      const response = await api.put(`/admin/kyc/settings/rejection-templates/${id}`, dto);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-rejection-templates'] });
    },
  });
}

/**
 * Delete a rejection template
 */
export function useDeleteRejectionTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/admin/kyc/settings/rejection-templates/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-rejection-templates'] });
    },
  });
}
